import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { notification } from "antd";
import { useAppSelector } from "../../../redux/hook";
import { RequestOtpDocument } from "../../../components/__generated__/requestOTP";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { UserDocument } from "../../../graphql/queries/__generated__/auth.query";
import { setUser } from "../../../redux/slice/auth.slice";
import ScreenWrapper from "../../../components/common/ScreenWrapper";
import AccountPage from "./AccountPage";

const RequestOTP = () => {
  const user = useAppSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { data: userData } = useQuery(UserDocument, {
    variables: {
      userId: user?.id,
    },
  });

  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);

  const [requestPhoneOTP, { loading: isRequestingPhoneOTP }] = useMutation(
    RequestOtpDocument,
    {
      variables: {
        input: {
          phone: user?.phone,
        },
      },
      onError(error) {
        notification.error({
          message: "Failed to send phone OTP",
          description: error.message,
        });
      },
      onCompleted(data) {
        if (data && data.requestOTP) {
          localStorage.setItem("phoneOtpToken", String(data.requestOTP.token));
          notification.success({
            message: "Success",
            description:
              "A code has been sent to your phone as SMS. Input the code below",
          });
          navigate("/verify-phone");
        }
      },
    }
  );

  const [requestEmailOTP, { loading: isRequestingEmailOTP }] = useMutation(
    RequestOtpDocument,
    {
      variables: {
        input: {
          email: user?.email,
        },
      },
      onError(error) {
        notification.error({
          message: "Failed to send email OTP",
          description: error.message,
        });
      },
      onCompleted(data) {
        if (data && data.requestOTP) {
          localStorage.setItem("emailOtpToken", String(data.requestOTP.token));
          notification.success({
            message: "Success",
            description:
              "A code has been sent to your email. Input the code below",
          });
          navigate("/verify-email");
        }
      },
    }
  );

  useEffect(() => {
    if (userData?.user?.verifiedDetails) {
      const phoneVerified =
        userData.user.verifiedDetails.has_verified_phonenumber || false;
      const emailVerified =
        userData.user.verifiedDetails.has_verified_email || false;
      setIsPhoneVerified(phoneVerified);
      setIsEmailVerified(emailVerified);

      if (
        phoneVerified &&
        emailVerified &&
        !user?.verified &&
        userData.user.verified
      ) {
        dispatch(setUser(userData.user));
      }
    }
  }, [userData, user, dispatch]);

  const statusClass = (verified: boolean) =>
    `px-3 py-1 rounded-full text-xs font-medium ${
      verified ? "bg-[#EAF5F4] text-gray-800" : "bg-red-50 text-red-700"
    }`;

  return (
      <ScreenWrapper showBackBtn title="Verify">
        <AccountPage>
          <h1 className="text-base font-medium text-gray-800 mb-4">
            Verification
          </h1>

          <button
            type="button"
            className="w-full text-left bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-3 disabled:opacity-70"
            onClick={() => !isEmailVerified && requestEmailOTP()}
            disabled={isEmailVerified || isRequestingEmailOTP}
          >
            <div className="flex justify-between items-start gap-3">
              <div>
                <h2 className="text-sm font-medium text-gray-900">Email</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Verify your email address to further protect your account.
                </p>
              </div>
              <span className={statusClass(isEmailVerified)}>
                {isEmailVerified
                  ? "Verified"
                  : isRequestingEmailOTP
                    ? "Sending..."
                    : "Unverified"}
              </span>
            </div>
          </button>

          <button
            type="button"
            className="w-full text-left bg-white rounded-2xl border border-gray-100 shadow-sm p-4 disabled:opacity-70"
            onClick={() => !isPhoneVerified && requestPhoneOTP()}
            disabled={isPhoneVerified || isRequestingPhoneOTP}
          >
            <div className="flex justify-between items-start gap-3">
              <div>
                <h2 className="text-sm font-medium text-gray-900">
                  Phone number
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Verify your phone number to further protect your account.
                </p>
              </div>
              <span className={statusClass(isPhoneVerified)}>
                {isPhoneVerified
                  ? "Verified"
                  : isRequestingPhoneOTP
                    ? "Sending..."
                    : "Unverified"}
              </span>
            </div>
          </button>
        </AccountPage>
      </ScreenWrapper>
  );
};

export default RequestOTP;
